import React, { useEffect, useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import "../sda/styles.scss";
import { Text } from "@amzn/stencil-react-components/text";
import { ManageApplicationCardModal } from "./card-modal";
import {
  bonusBackgroundColor,
  bonusTextColor,
  majorSectionBackgroundColor,
} from "../../config/palette";
import "./styles.scss";
import useArb from "../../hooks/use-arb";
import { getSettings, MX_STAGES } from "../../helpers/get-settings";
import {
  IconCalendarFill,
  IconChevronDown,
  IconChevronUp,
  IconClockFill,
  IconGlobe,
  IconHourGlass,
  IconLocationFill,
  IconPaymentFill,
  IconQuestionCircle,
  IconSize,
} from "@amzn/stencil-react-components/icons";
import { IconDuration } from "../icons/duration";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import { Link } from "@amzn/stencil-react-components/link";
import { ExpanderContent } from "@amzn/stencil-react-components/expander";
import { Card } from "@amzn/stencil-react-components/card";
import { formatedStartTime } from "../../utility/time-processor";
import { DisplayNewUIComponent } from "../../utility/new-ui-toggle";

interface ManageApplicationCardHeaderProps {
  jobTitle?: string;
  hours?: string;
  startDate?: string;
  startTime?: string;
  description?: string;
  address?: string;
  payRate?: string;
  signOnBonus?: string;
  scheduleBannerText?: string;
  hoursPerWeek?: string;
  image?: string;
  startDateStatus?: string;
  onChangeScheduleClick: () => void;
  onWithdrawApplication: () => void;
  onWithdrawApplicationConfirm: (withdrawReason: string) => void;
  onGoToDashboardClick: () => void;
  eligibleForCSS: boolean;
  eligibleForWithdraw: boolean;
  applicationId: string;
  supportedLanguage: string;
  employmentTypeL10N: string;
  parsedTrainingDate: string;
  monthlyBasePayL10N: string;
  currencyCode: string;
  collapsed: boolean;
  eligibleForAppointmentRescheduling: boolean;
  appointmentSelfServiceURL: string;
}

export const ManageApplicationCardHeader = ({
  applicationId,
  jobTitle,
  hours,
  startDate,
  startTime,
  description,
  address,
  payRate,
  signOnBonus,
  scheduleBannerText,
  hoursPerWeek,
  image,
  startDateStatus,
  employmentTypeL10N,
  onChangeScheduleClick,
  onWithdrawApplication,
  onWithdrawApplicationConfirm,
  onGoToDashboardClick,
  eligibleForCSS,
  eligibleForWithdraw,
  supportedLanguage,
  parsedTrainingDate,
  monthlyBasePayL10N,
  currencyCode,
  collapsed,
  eligibleForAppointmentRescheduling,
  appointmentSelfServiceURL,
}: ManageApplicationCardHeaderProps) => {
  const hourText = hours ? hours.split("\n") : [];
  const bundle = useArb("HVHChecklistPantherResource");

  const hoursTranslated = bundle.getMessage("Checklist-JobCard-Hours");
  const payHoursTranslated = bundle.getMessage(
    "Checklist-JobCard-PayRate-Hour"
  );
  const weekTranslated = bundle.getMessage("Checklist-JobCard-Week");
  const monthTranslated = bundle.getMessage("Checklist-AppointmentCard-Month");
  const shiftTranslated = bundle.getMessage("Checklist-JobCard-Shift");
  const payTranslated = bundle.getMessage("Checklist-JobCard-PayRate");
  const durationTranslated = bundle.getMessage("Checklist-JobCard-Duration");
  const possibleStartDateTranslated = bundle.getMessage(
    "Checklist-JobCard-PossibleStartDate"
  );
  const startDateTranslated = bundle.getMessage("Checklist-JobCard-StartDate");
  const addressTranslated = bundle.getMessage("Checklist-JobCard-WorkAddress");
  const bonusTranslated = bundle.getMessage("Checklist-JobCard-Bonus");
  const languageSupported = bundle.getMessage("Checklist-JobCard-Language");
  const iconTranslated = bundle.getMessage("Checklist-JobCard-Icon");
  const [isMexico, setMexicoFlag] = useState<boolean>();
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  useEffect(() => {
    getSettings().then((settings) =>
      setMexicoFlag(MX_STAGES.includes(settings.stage))
    );
  });

  const IconWithTooltip = withTooltip()(IconQuestionCircle);

  const startDateText =
    startDateStatus === "TENTATIVE" ? (
      <Row alignItems="start">
        <Text fontSize="T200">
          <strong>{possibleStartDateTranslated}</strong>:&nbsp;{startDate},{" "}
          {formatedStartTime(startTime || "")}
        </Text>
        <IconWithTooltip
          title="start date tooltip"
          tooltipText={bundle.getMessage("Checklist-StartDate-ToolTip")}
          color={bonusTextColor}
        />
      </Row>
    ) : (
      <Text fontSize="T200">
        <strong>{startDateTranslated}</strong>:&nbsp;{startDate},{" "}
        {formatedStartTime(startTime || "")}
      </Text>
    );

  const requiredTrainingDateText = bundle.getMessage(
    "Checklist-JobCard-RequiredTrainingDates"
  );
  const trainingDates = parsedTrainingDate
    ? parsedTrainingDate.split("\n")
    : [];

  const googlePlaceURL = "http://maps.google.com/?q=";
  const googleMapLink = (
    <Link fontSize={"T200"} href={googlePlaceURL + address} target={"_blank"}>
      {address}
    </Link>
  );

  const expandableContent = (
    <ExpanderContent isExpanded={isExpanded}>
      <Col gridGap="1rem" padding="0 1rem 1rem 1rem">
        <Row gridGap="0.5rem" alignItems="start">
          <IconCalendarFill size={IconSize.Small} />
          {startDateText}
        </Row>

        <Row gridGap="0.5rem">
          <IconClockFill size={IconSize.Small} />
          <Col gridGap="0.5rem">
            <Row>
              <Text fontSize="T200">
                <strong>{shiftTranslated}</strong>:&nbsp;
              </Text>
              <Text fontSize="T200">
                {hourText.map((text: string, index) => (
                  <Row key={text + index}>{text}</Row>
                ))}
              </Text>
            </Row>
            {parsedTrainingDate && (
              <Row>
                <Col>
                  <Text fontSize="T200">
                    <em>{requiredTrainingDateText}</em>
                  </Text>
                  <Text fontSize="T200">
                    {trainingDates.map((date: string, index) => (
                      <Row key={date + index}>{date}</Row>
                    ))}
                  </Text>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        <Row gridGap="0.5rem">
          <Col>
            <IconPaymentFill size={IconSize.Small} />
          </Col>
          <Col gridGap="0.5rem">
            <Row>
              <Text fontSize="T200">
                <strong>{payTranslated}</strong>:&nbsp;
                {isMexico ? currencyCode : ""}
                {isMexico ? monthlyBasePayL10N : payRate}/
                {isMexico ? monthTranslated : payHoursTranslated}
              </Text>
            </Row>
            {scheduleBannerText && (
              <Row>
                <Text fontSize="T200" id="schedule-card-hours">
                  <em>{scheduleBannerText}</em>
                </Text>
              </Row>
            )}
            {signOnBonus && (
              <Row>
                <Row
                  padding="0.3rem 0rem 0.3rem 0.5rem"
                  backgroundColor={bonusBackgroundColor}
                  alignItems="center"
                >
                  <Text fontSize="T200" color={bonusTextColor}>
                    {bonusTranslated}&nbsp;{signOnBonus}
                  </Text>
                </Row>
              </Row>
            )}
          </Col>
        </Row>

        <Row gridGap="0.5rem" alignItems="center">
          <IconHourGlass size={IconSize.Small} />
          <Col>
            <Text fontSize="T200">
              <strong>{hoursTranslated}</strong>:&nbsp;{hoursPerWeek}&nbsp;
              {hoursTranslated}/{weekTranslated}
            </Text>
          </Col>
        </Row>

        {employmentTypeL10N && (
          <Row gridGap="0.5rem" alignItems="center">
            <IconDuration />
            <Col>
              <Text fontSize="T200">
                <strong>{durationTranslated}</strong>:&nbsp;
                {employmentTypeL10N}
              </Text>
            </Col>
          </Row>
        )}

        <Row gridGap="0.5rem" alignItems="start">
          <IconLocationFill size={IconSize.Small} />
          <Col>
            <Text fontSize="T200">
              <strong>{addressTranslated}</strong>:&nbsp;{googleMapLink}
            </Text>
          </Col>
        </Row>

        {!isMexico && supportedLanguage && supportedLanguage.length > 0 && (
          <Row gridGap="0.5rem" alignItems="start">
            <IconGlobe size={IconSize.Small} />
            <Col>
              <Text fontSize="T200">
                <strong>{languageSupported}</strong>:&nbsp;{supportedLanguage}
              </Text>
            </Col>
          </Row>
        )}
      </Col>
    </ExpanderContent>
  );

  const card = (
    <Col
      width="100%"
      padding="0rem"
      alignItems={collapsed ? "center" : "start"}
    >
      <Row alignItems="center" gridGap={"2rem"}>
        <Row alignItems="center">
          {!collapsed && (
            <Col padding="1rem 0 1rem 1rem" alignItems="center">
              <img alt={iconTranslated} src={image} width="70px" />
            </Col>
          )}
          <Col padding="1rem 1rem 1rem 1rem" gridGap="0.2rem">
            <Text fontSize="T200" fontWeight="bold" id="schedule-card-jobTitle">
              {jobTitle}
              {collapsed && (
                <Link
                  className="trigger-link"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <Row alignItems="center">
                    {bundle.getMessage("Checklist-JobCard-ViewJobDetails")}
                    {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
                  </Row>
                </Link>
              )}
            </Text>
            {!collapsed && (
              <Text fontSize="T200" id="schedule-card-hours">
                {description}
              </Text>
            )}
          </Col>
        </Row>
        {(eligibleForCSS || eligibleForWithdraw) && (
          <ManageApplicationCardModal
            eligibleForCSS={eligibleForCSS}
            eligibleForWithdraw={eligibleForWithdraw}
            applicationId={applicationId}
            onChangeScheduleClick={onChangeScheduleClick}
            onGoToDashboardClick={onGoToDashboardClick}
            onWithdrawApplication={onWithdrawApplication}
            onWithdrawApplicationConfirm={onWithdrawApplicationConfirm}
            eligibleForAppointmentRescheduling={
              eligibleForAppointmentRescheduling
            }
            appointmentSelfServiceURL={appointmentSelfServiceURL}
          />
        )}
      </Row>
      {expandableContent}
    </Col>
  );

  let content;
  if (DisplayNewUIComponent) {
    content = (
      <Col backgroundColor={majorSectionBackgroundColor} padding="0rem">
        {card}
      </Col>
    );
  } else {
    content = <Card padding="0rem">{card}</Card>;
  }
  return <>{content}</>;
};
